import React, { useEffect, useMemo, useState } from "react";
import EmptyHint from "../../../components/EmptyHint";
import {
  useGetAlertProfilesQuery,
  useGetDistortionTimeSeriesQuery,
  type AlertProfile,
} from "../apiSlice";
import LineChart, { type NamedSeries } from "./LineChart";
import DataTable from "./DataTable";
import dayjs from "dayjs";

interface DistortionViewProps {
  projectId: number;
  view: "charts" | "table";
  from: string;
  to: string;
  showDailyGridlines: boolean;
  onDatesResolved?: (start: string, end: string) => void;
}

// Slope-ratio formatter: beyond 1:2000 the distortion is effectively flat.
const formatDistortionRatio = (v: number): string => {
  const rounded = Math.round(v);
  if (Math.abs(rounded) > 2000) return "< 1 in 2000";
  return String(rounded);
};

const SLOPE_RATIO_PLOT_MAX = 2000;
const CAPPED_RATIO_NOTE =
  "Values plotted at 2000 mean the actual value may be greater than 2000.";

function hasSeriesAboveMax(series: NamedSeries[], max: number): boolean {
  return series.some((s) => s.points.some((p) => p.value > max));
}

function clampSeriesMax(series: NamedSeries[], max: number): NamedSeries[] {
  return series.map((s) => ({
    ...s,
    points: s.points.map((p) => ({
      ...p,
      value: p.value > max ? max : p.value,
    })),
  }));
}

const DistortionView: React.FC<DistortionViewProps> = ({
  projectId,
  view,
  from,
  to,
  showDailyGridlines,
  onDatesResolved,
}) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data: timeSeries = [], isFetching } = useGetDistortionTimeSeriesQuery(
    { projectId },
  );

  const { data: profiles = [] } = useGetAlertProfilesQuery({ projectId });
  const profileMap = useMemo(() => {
    const m = new Map<number, AlertProfile>();
    profiles.forEach((p) => m.set(p.id, p));
    return m;
  }, [profiles]);

  // Initialise pair selection + dates once data arrives.
  useEffect(() => {
    setSelected(new Set(timeSeries.slice(0, 5).map((p) => p.pairId)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeSeries.length]);

  // When the parent hasn't supplied date bounds (e.g. the distortion tab is
  // opened before any other data loads), derive them from the fetched data.
  const effectiveStart = useMemo(() => {
    const allDates = timeSeries.flatMap((p) => p.data.map((d) => d.date));
    const result = allDates.length
      ? allDates.reduce((a, b) => (+new Date(a) < +new Date(b) ? a : b))
      : "";
    return result;
  }, [timeSeries]);

  const effectiveEnd = useMemo(() => {
    const allDates = timeSeries.flatMap((p) => p.data.map((d) => d.date));
    const result = allDates.length
      ? allDates.reduce((a, b) => (+new Date(a) > +new Date(b) ? a : b))
      : dayjs().format("YYYY-MM-DD");
    return result;
  }, [timeSeries]);

  useEffect(() => {
    if (onDatesResolved) onDatesResolved(effectiveStart, effectiveEnd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveStart, effectiveEnd]);

  const series: NamedSeries[] = useMemo(() => {
    const inRange = (date: string) => {
      const day = dayjs(date).format("YYYY-MM-DD");
      return (!from || day >= from) && (!to || day <= to);
    };
    return timeSeries
      .filter((p) => selected.has(p.pairId))
      .map((p) => {
        const name = p.pairLabel || `${p.pointAName}-${p.pointBName}`;
        const points = p.data
          .filter((d) => inRange(d.date))
          .map((d) => {
            // Settlement distortion uses reading mode only (cumulative distortion ratio).
            const value =
              d.distortion === 0 ? 9999 : Math.round(1 / d.distortion);
            return { date: d.date, value };
          })
          .filter((pt) => Number.isFinite(pt.value));
        return { name, points, alertProfileId: p.alertProfileId };
      });
  }, [timeSeries, selected, from, to]);

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  if (isFetching && !timeSeries.length) {
    return (
      <EmptyHint
        icon="bi-hourglass-split"
        title="Loading…"
        subtitle="Fetching distortion pairs."
      />
    );
  }
  if (!timeSeries.length) {
    return (
      <EmptyHint
        icon="bi-info-circle"
        title="No distortion pairs"
        subtitle="Configure settlement distortion pairs for this project first."
      />
    );
  }

  return (
    <div>
      <div className="filter-bar">
        <div className="field" style={{ flex: 1, minWidth: 280 }}>
          <label className="row between">
            <span>Pairs</span>
          </label>
          <div className="chips">
            {timeSeries.map((p) => {
              const name = p.pairLabel || `${p.pointAName}-${p.pointBName}`;
              return (
                <button
                  key={p.pairId}
                  className={`chip ${selected.has(p.pairId) ? "active" : ""}`}
                  onClick={() => toggle(p.pairId)}
                >
                  {name}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      {view === "charts" &&
        (() => {
          // Group selected series by alert profile
          const groups = new Map<number | null, NamedSeries[]>();
          series.forEach((s) => {
            const pid = s.alertProfileId ?? null;
            if (!groups.has(pid)) groups.set(pid, []);
            groups.get(pid)!.push(s);
          });

          const renderChart = (groupSeries: NamedSeries[]) => {
            const hasCappedValues = hasSeriesAboveMax(
              groupSeries,
              SLOPE_RATIO_PLOT_MAX,
            );
            const chartSeries = clampSeriesMax(
              groupSeries,
              SLOPE_RATIO_PLOT_MAX,
            );

            return (
              <LineChart
                series={chartSeries}
                yAxisLabel="Distortion (1:N)"
                yAxisUnit="1:N"
                hoverFormat=".0f"
                invertY
                signNote={hasCappedValues ? CAPPED_RATIO_NOTE : undefined}
                alertProfiles={profileMap}
                alertToDisplay={(level) =>
                  level.entry_mode === "gradient" && level.value > 0
                    ? Math.min(
                        SLOPE_RATIO_PLOT_MAX,
                        Math.round(1 / level.value),
                      )
                    : null
                }
                activities={[]}
                startDate={from || effectiveStart}
                endDate={to || effectiveEnd}
                yAxisRange={[0, SLOPE_RATIO_PLOT_MAX]}
                showDailyGridlines={showDailyGridlines}
              />
            );
          };

          return (
            <>
              {Array.from(groups.entries()).map(([profileId, groupSeries]) => {
                const profile =
                  profileId != null ? profileMap.get(profileId) : undefined;
                const profileName = profile?.description ?? "No action profile";
                return (
                  <div key={profileId ?? "null"} style={{ marginBottom: 32 }}>
                    <div
                      className="fw-600"
                      style={{ margin: "0 0 6px", fontSize: "1rem" }}
                    >
                      Projected Angular Distortion — {profileName}
                    </div>
                    {renderChart(groupSeries)}
                  </div>
                );
              })}
            </>
          );
        })()}
      {view === "table" && (
        <DataTable
          series={series}
          unit="1:N"
          numberFormat={formatDistortionRatio}
        />
      )}
    </div>
  );
};

export default DistortionView;
